import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  User, 
  Truck, 
  Clock, 
  Download,
  Printer,
  Package,
  Scale,
  TrendingUp,
  TrendingDown
} from "lucide-react";
import type { Order, Client, Supplier } from "../../../shared/schema";
import StockAllocation from "./stock-allocation";
import VesselTracking from "./vessel-tracking";

interface ExpandedOrderData extends Order {
  client?: Client;
  supplier?: Supplier;
  transporter?: any;
  weighbridgeTickets?: any[];
  charges?: any[];
  documents?: any[];
  activityLog?: any[];
  computed?: {
    totalCharges: number;
    netWeight: number | null;
    weightVariance: number | null;
    variancePercentage: number | null;
    isWithinTolerance: boolean;
    documentCount: number;
    lastActivity: string | null;
  };
  customerName?: string;
  collectionPoint?: string;
}

interface OrderDetailProps {
  order: ExpandedOrderData;
}

export default function OrderDetailView({ order }: OrderDetailProps) {
  const generatePDF = () => {
    const content = `
ORDER DETAIL PROCESS - ${order.orderNumber}
================================================================

Order Number: ${order.orderNumber}
Date Created: ${order.createdAt ? new Date(order.createdAt).toLocaleString() : 'N/A'}

BASIC INFORMATION
-----------------
Order Number: ${order.orderNumber}
Product: ${order.product || 'N/A'}
Product Code: ${order.productCode || 'N/A'}
Description: ${order.description || 'N/A'}
Reference Number: ${order.referenceNumber || 'N/A'}
Purchase Order: ${order.purchaseOrderNumber || 'N/A'}

QUANTITY & WEIGHT
-----------------
Quantity: ${order.quantity ? order.quantity.toLocaleString() : 'N/A'} ${order.units || order.unit || ''}
Expected Weight: ${order.expectedWeight ? order.expectedWeight.toLocaleString() : 'N/A'} ${order.unit || 'kg'}

CLIENT INFORMATION
------------------
Client: ${order.customerName || order.client?.name || 'N/A'}
Contact: ${order.client?.contactPerson || 'N/A'}
Phone: ${order.client?.phone || 'N/A'}
Email: ${order.client?.email || 'N/A'}

SUPPLIER INFORMATION
--------------------
Collection Point: ${order.collectionPoint || order.supplier?.name || 'N/A'}
Contact: ${order.supplier?.contactPerson || 'N/A'}
Phone: ${order.supplier?.phone || 'N/A'}
Email: ${order.supplier?.email || 'N/A'}

DELIVERY INFORMATION
--------------------
Origin: ${order.originAddress || 'N/A'}
Destination: ${order.destinationAddress || 'N/A'}
Requested Pickup: ${order.requestedPickupDate ? new Date(order.requestedPickupDate).toLocaleDateString() : 'N/A'}
Requested Delivery: ${order.requestedDeliveryDate ? new Date(order.requestedDeliveryDate).toLocaleDateString() : 'N/A'}
Actual Pickup: ${order.actualPickupDate ? new Date(order.actualPickupDate).toLocaleDateString() : 'Not completed'}
Actual Delivery: ${order.actualDeliveryDate ? new Date(order.actualDeliveryDate).toLocaleDateString() : 'Not completed'}

FINANCIAL INFORMATION
---------------------
Agreed Rate: ${order.agreedRate ? order.agreedRate.toLocaleString() : 'N/A'} ${order.rateUnit || ''}
Additional Charges: ${order.additionalCharges ? order.additionalCharges.toLocaleString() : 'N/A'}
Total Amount: ${order.totalAmount ? order.totalAmount.toLocaleString() : 'N/A'}

SPECIAL REQUIREMENTS
--------------------
Priority: ${order.priority || 'Normal'}
Requires Permit: ${order.requiresPermit ? 'Yes' : 'No'}
Hazardous Material: ${order.hazardousMaterial ? 'Yes' : 'No'}
Temperature Controlled: ${order.temperatureControlled ? 'Yes' : 'No'}

NOTES & INSTRUCTIONS
--------------------
Notes: ${order.notes || 'None'}
Special Instructions: ${order.specialInstructions || 'None'}

Generated by Weigh8 Order Management System
Report Generated: ${new Date().toLocaleString()}
    `.trim();

    const blob = new Blob([content], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `Order_${order.orderNumber}_Detail.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'confirmed': return 'bg-blue-100 text-blue-800';
      case 'in_transit': return 'bg-purple-100 text-purple-800';
      case 'delivered': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority?.toLowerCase()) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getVarianceIndicator = () => {
    const variance = order.computed?.variancePercentage;
    if (!variance) return null;
    
    const isNegative = variance < 0;
    const absVariance = Math.abs(variance);
    const color = order.computed?.isWithinTolerance ? 'text-green-600' : 'text-red-600';
    const Icon = isNegative ? TrendingDown : TrendingUp;
    
    return (
      <div className={`flex items-center ${color}`}>
        <Icon className="w-4 h-4 mr-1" />
        {absVariance.toFixed(1)}%
      </div>
    );
  };

  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Enhanced Header Section */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-3">
                <Package className="w-8 h-8 text-blue-600" />
                <div>
                  <h1 className="text-3xl font-bold text-gray-900" data-testid="text-title">
                    {order.orderNumber}
                  </h1>
                  <p className="text-lg text-gray-600 font-medium">
                    {order.product || 'Transportation Service'}
                  </p>
                </div>
              </div>
              <div className="flex items-center space-x-3">
                {order.createdAt && (
                  <div className="text-sm text-gray-500 flex items-center gap-1">
                    <Clock className="w-4 h-4" />
                    Created {new Date(order.createdAt).toLocaleDateString()}
                  </div>
                )}
              </div>
            </div>
          <div className="flex space-x-2">
            <Button onClick={generatePDF} variant="outline" size="sm" data-testid="button-export">
              <Download className="w-4 h-4 mr-2" />
              Export
            </Button>
            <Button onClick={() => window.print()} variant="outline" size="sm" data-testid="button-print">
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          </div>
        </div>
        
          {/* Enhanced KPI Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
            <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <Scale className="w-5 h-5 text-blue-600" />
                <div className="text-xs text-blue-600 font-medium uppercase tracking-wide">Net Weight</div>
              </div>
              <div className="text-2xl font-bold text-blue-900" data-testid="text-net-weight">
                {order.computed?.netWeight ? `${order.computed.netWeight.toLocaleString()}` : 'Pending'}
              </div>
              <div className="text-sm text-blue-700 mt-1">
                {order.computed?.netWeight ? 'kg' : 'Awaiting weighbridge'}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-green-600" />
                <div className="text-xs text-green-600 font-medium uppercase tracking-wide">Variance</div>
              </div>
              <div className="text-2xl font-bold text-green-900" data-testid="text-variance">
                {getVarianceIndicator() || (
                  <span className="text-gray-500">N/A</span>
                )}
              </div>
              <div className="text-sm text-green-700 mt-1">
                {order.computed?.isWithinTolerance !== undefined ? 
                  (order.computed.isWithinTolerance ? 'Within tolerance' : 'Outside tolerance') : 
                  'Awaiting measurement'
                }
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-purple-50 to-purple-100 border border-purple-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                <div className="text-xs text-purple-600 font-medium uppercase tracking-wide">Total Value</div>
              </div>
              <div className="text-2xl font-bold text-purple-900" data-testid="text-total-charges">
                {order.computed?.totalCharges?.toLocaleString() || order.totalAmount?.toLocaleString() || '0'}
              </div>
              <div className="text-sm text-purple-700 mt-1">
                {order.agreedRate ? `Rate: ${order.agreedRate}/${order.rateUnit || 'unit'}` : 'Rate pending'}
              </div>
            </div>
            
            <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 p-4 rounded-xl">
              <div className="flex items-center justify-between mb-2">
                <FileText className="w-5 h-5 text-orange-600" />
                <div className="text-xs text-orange-600 font-medium uppercase tracking-wide">Documents</div>
              </div>
              <div className="text-2xl font-bold text-orange-900" data-testid="text-document-count">
                {order.computed?.documentCount || 0}
              </div>
              <div className="text-sm text-orange-700 mt-1">
                {order.computed?.lastActivity ? 
                  `Last: ${new Date(order.computed.lastActivity).toLocaleDateString()}` : 
                  'No activity yet'
                }
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content Container */}
      <div className="max-w-7xl mx-auto p-6">

        {/* Enhanced Tabbed Content */}
        <Tabs defaultValue="details" className="w-full">
        <TabsList className="grid w-full grid-cols-5">
          <TabsTrigger value="details" data-testid="tab-details">Details</TabsTrigger>
          <TabsTrigger value="weighbridge" data-testid="tab-weighbridge">Weighbridge</TabsTrigger>
          <TabsTrigger value="vessel" data-testid="tab-vessel">Vessel</TabsTrigger>
          <TabsTrigger value="stock" data-testid="tab-stock">Stock</TabsTrigger>
          <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
        </TabsList>

        {/* Details Tab */}
        <TabsContent value="details" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <FileText className="w-5 h-5 mr-2" />
                  Order Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Order Number</label>
                    <div className="font-mono text-base font-bold" data-testid="text-order-number">
                      {order.orderNumber}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Created</label>
                    <div className="text-base">
                      {order.createdAt ? new Date(order.createdAt).toLocaleDateString() : 'N/A'}
                    </div>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Product</label>
                  <div className="text-base font-medium" data-testid="text-product">
                    {order.product || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Product Code</label>
                  <div className="text-base font-mono" data-testid="text-product-code">
                    {order.productCode || 'N/A'}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Description</label>
                  <div className="text-base" data-testid="text-description">
                    {order.description || 'N/A'}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Quantity</label>
                    <div className="font-medium text-xl text-green-600" data-testid="text-quantity">
                      {order.quantity ? `${order.quantity.toLocaleString()} ${order.units || order.unit || ''}` : 'N/A'}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Expected Weight</label>
                    <div className="font-medium text-lg text-blue-600" data-testid="text-expected-weight">
                      {order.expectedWeight ? `${order.expectedWeight.toLocaleString()} ${order.unit || 'kg'}` : 'N/A'}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  Parties
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-600">Client</label>
                  <div className="font-medium" data-testid="text-client-name">
                    {order.customerName || order.client?.name || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {order.client?.contactPerson} • {order.client?.phone}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Collection Point</label>
                  <div className="font-medium" data-testid="text-supplier-name">
                    {order.collectionPoint || order.supplier?.name || 'Current Mine'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {order.supplier?.contactPerson} • {order.supplier?.phone}
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Transporter</label>
                  <div className="font-medium" data-testid="text-transporter-name">
                    {order.transporter?.name || 'Not assigned'}
                  </div>
                  <div className="text-sm text-gray-500">
                    {order.transporter?.contactPerson} • {order.transporter?.phone}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Weighbridge Tab */}
        <TabsContent value="weighbridge" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <Scale className="w-5 h-5 mr-2" />
                Weight Measurements
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.weighbridgeTickets && order.weighbridgeTickets.length > 0 ? (
                <div className="space-y-4">
                  {order.weighbridgeTickets.map((ticket: any, index: number) => (
                    <div key={ticket.id || index} className="border rounded-lg p-4">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <div className="font-medium">Ticket #{ticket.ticketNumber}</div>
                          <div className="text-sm text-gray-500">{ticket.weighbridgeName}</div>
                        </div>
                        <Badge variant={ticket.isWithinTolerance ? "default" : "destructive"}>
                          {ticket.isWithinTolerance ? 'Within Tolerance' : 'Variance Alert'}
                        </Badge>
                      </div>
                      <div className="grid grid-cols-3 gap-4">
                        <div>
                          <label className="text-sm font-medium text-gray-600">Gross Weight</label>
                          <div className="font-medium">{ticket.grossWeight ? `${ticket.grossWeight} kg` : 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Tare Weight</label>
                          <div className="font-medium">{ticket.tareWeight ? `${ticket.tareWeight} kg` : 'N/A'}</div>
                        </div>
                        <div>
                          <label className="text-sm font-medium text-gray-600">Net Weight</label>
                          <div className="font-medium text-blue-600">{ticket.netWeight ? `${ticket.netWeight} kg` : 'N/A'}</div>
                        </div>
                      </div>
                      {ticket.weightVariance && (
                        <div className="mt-3 p-2 bg-gray-50 rounded">
                          <div className="text-sm">
                            Variance: <span className={ticket.isWithinTolerance ? 'text-green-600' : 'text-red-600'}>
                              {ticket.variancePercentage ? `${ticket.variancePercentage.toFixed(1)}%` : 'N/A'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No weighbridge readings available
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Documents Tab */}
        <TabsContent value="documents" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <FileText className="w-5 h-5 mr-2" />
                Attached Documents
              </CardTitle>
            </CardHeader>
            <CardContent>
              {order.documents && order.documents.length > 0 ? (
                <div className="space-y-3">
                  {order.documents.map((doc: any, index: number) => (
                    <div key={doc.id || index} className="flex items-center justify-between p-3 border rounded-lg">
                      <div className="flex items-center space-x-3">
                        <FileText className="w-5 h-5 text-gray-400" />
                        <div>
                          <div className="font-medium">{doc.title || doc.originalFileName}</div>
                          <div className="text-sm text-gray-500">
                            {doc.documentType} • {doc.fileSize ? `${(doc.fileSize / 1024).toFixed(1)} KB` : 'Unknown size'}
                          </div>
                        </div>
                      </div>
                      <Button variant="outline" size="sm" data-testid={`button-download-${index}`}>
                        <Download className="w-4 h-4 mr-2" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No documents attached
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Vessel Tab */}
        <TabsContent value="vessel" className="space-y-6">
          <VesselTracking
            order={{
              id: order.id,
              orderNumber: order.orderNumber,
              product: order.product || 'Unknown Product',
              quantity: parseFloat(order.quantity?.toString() || '0'),
              unit: order.unit || 'kg',
              status: order.status,
              
              // Vessel information
              vesselName: order.vesselName || undefined,
              vesselImoNumber: order.vesselImoNumber || undefined,
              vesselType: order.vesselType || undefined,
              vesselFlag: order.vesselFlag || undefined,
              vesselDwt: order.vesselDwt ? parseFloat(order.vesselDwt.toString()) : undefined,
              
              // Port information
              portOfLoading: order.portOfLoading || undefined,
              portOfDischarge: order.portOfDischarge || undefined,
              loadingBerth: order.loadingBerth || undefined,
              dischargeBerth: order.dischargeBerth || undefined,
              
              // Vessel scheduling
              vesselEta: order.vesselEta?.toString() || undefined,
              vesselEtd: order.vesselEtd?.toString() || undefined,
              vesselAta: order.vesselAta?.toString() || undefined,
              vesselAtd: order.vesselAtd?.toString() || undefined,
              
              // Current position
              currentLatitude: order.currentLatitude ? parseFloat(order.currentLatitude.toString()) : undefined,
              currentLongitude: order.currentLongitude ? parseFloat(order.currentLongitude.toString()) : undefined,
              lastPositionUpdate: order.lastPositionUpdate?.toString() || undefined,
              vesselStatus: order.vesselStatus || undefined,
              
              // Progress tracking
              progressStage: order.progressStage || undefined,
              progressPercentage: order.progressPercentage || undefined,
              milestones: Array.isArray(order.milestones) ? order.milestones : undefined,
              
              // Cargo details
              cargoDescription: order.cargoDescription || undefined,
              loadingDate: order.loadingDate?.toString() || undefined,
              dischargingDate: order.dischargingDate?.toString() || undefined,
            }}
          />
        </TabsContent>

        {/* Stock Tab */}
        <TabsContent value="stock" className="space-y-6">
          <StockAllocation 
            order={{
              id: order.id,
              orderNumber: order.orderNumber,
              product: order.product || 'Unknown Product',
              quantity: parseFloat(order.quantity?.toString() || '0'),
              unit: order.unit || 'kg',
              status: order.status,
              clientId: order.clientId ?? undefined,
              supplierId: order.supplierId ?? undefined
            } as any}
          />
        </TabsContent>

      </Tabs>
      </div>
    </div>
  );
}